import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, FormControl, Select, MenuItem, Skeleton } from "@mui/material";
import { Icon } from "@iconify/react";
import moment from "moment-timezone";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Sector,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
} from "recharts";
import {
  useGetAnalytics,
  useGetTimeseries,
  useGetAnalyticsTables,
} from "../../api/analytics";
import {
  buildArgs,
  GRANULARITY_OPTIONS,
  pct,
  useFullscreen,
  fullscreenSx,
  FullscreenBackdrop,
  FullscreenButton,
} from "./shared";

/* ============================== palette ==============================
   Re-uses the exact brand colours already used across the dashboards. */
const ACCENT = "#5751E9";
const SERIES = [
  { key: "normal", label: "Normal", color: "#53B253" },
  { key: "abnormal", label: "Abnormal", color: "#FFC46C" },
  { key: "critical", label: "Critical", color: "#FB8588" },
  { key: "beyond_critical", label: "Beyond Critical", color: "#B71C1C" },
  { key: "retake", label: "Retake", color: "#C2C9FF" },
];
// Daily volume is based on tests TAKEN, so it also shows a Pending slice
// (tests not yet interpreted). Used only by the daily Test Volume chart.
const TREND_SERIES = [...SERIES, { key: "pending", label: "Pending", color: "#B9BCC2" }];
const STATUS_FILL = {
  Active: "#53B253",
  "Partially Active": "#FFC46C",
  Dormant: "#FB8588",
  "Never Used": "#B9BCC2",
};
const GENDER_FILL = { Male: "#5751E9", Female: "#FB8588", Other: "#FFC46C", Unknown: "#B9BCC2" };
const REASON_COLORS = ["#5751E9", "#FFC46C", "#FB8588", "#53B253", "#C2C9FF"];

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const pctNum = (v) => {
  if (typeof v === "number") return v;
  const n = parseFloat(String(v ?? "").replace("%", ""));
  return Number.isFinite(n) ? n : 0;
};
const fmtMins = (m) => (m ? `${Math.floor(m / 60)}h ${Math.floor(m % 60)}m` : "—");

/* ------------- granularity-aware time-series helpers ------------- */
const periodKey = (m, g) =>
  g === "hour"
    ? m.format("YYYY-MM-DD HH")
    : g === "month"
      ? m.format("YYYY-MM")
      : g === "week"
        ? m.format("GGGG-[W]WW")
        : m.format("YYYY-MM-DD");
const periodLabel = (m, g) =>
  g === "hour" ? m.format("HH:mm") : g === "month" ? m.format("MMM YY") : m.format("DD MMM");

const buildTrend = (timeSeries, gran) => {
  const reports = timeSeries?.reports || [];
  const cases = timeSeries?.cases || [];
  const caseByKey = {};
  cases.forEach((c) => {
    const raw = c.date || c.day || c.hour || c.week || c.month;
    if (!raw) return;
    const key = periodKey(moment(raw), gran);
    caseByKey[key] = (caseByKey[key] || 0) + num(c.count);
  });
  return reports.map((entry) => {
    const raw = entry.hour || entry.day || entry.date || entry.week || entry.month;
    const m = moment(raw);
    const counts = entry.statusCounts || entry.counts || {};
    const normal = num(counts.normal);
    const abnormal = num(counts.abnormal);
    const critical = num(counts.critical);
    const beyond_critical = num(counts.beyond_critical);
    const retake = num(counts.retake);
    const pending = num(counts.pending);
    const total = normal + abnormal + critical + beyond_critical + retake + pending;
    return {
      label: periodLabel(m, gran),
      total,
      normal,
      abnormal,
      critical,
      beyond_critical,
      retake,
      pending,
      criticalPct: total ? +((critical / total) * 100).toFixed(1) : 0,
      abnormalPct: total ? +((abnormal / total) * 100).toFixed(1) : 0,
      cases: caseByKey[periodKey(m, gran)] || 0,
    };
  });
};

/* ====================== count-up animated number ====================== */
const useCountUp = (target, duration = 900) => {
  const [val, setVal] = useState(0);
  const fromRef = useRef(0);
  useEffect(() => {
    const start = fromRef.current;
    const end = Number(target) || 0;
    if (start === end) return undefined;
    let raf;
    let t0 = null;
    const tick = (t) => {
      if (t0 === null) t0 = t;
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(start + (end - start) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
      else fromRef.current = end;
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
};

const AnimatedNumber = ({ value, sx }) => {
  const v = useCountUp(value);
  return (
    <Typography sx={{ fontFamily: "Figtree", lineHeight: 1.1, ...sx }}>
      {Math.round(v).toLocaleString()}
    </Typography>
  );
};

/* =========================== shared tooltip =========================== */
const TooltipBox = ({ label, items }) => (
  <Box
    sx={{
      backgroundColor: "var(--app-surface)",
      px: "14px",
      py: "10px",
      borderRadius: "10px",
      // boxShadow: "0px 4px 14px rgba(0,0,0,0.12)",
      border: "1px solid var(--app-border)",
      minWidth: 150,
    }}
  >
    {label != null && (
      <>
        <Typography sx={{ fontWeight: 700, fontSize: 13, color: "var(--app-text)" }}>
          {label}
        </Typography>
        <Box sx={{ borderBottom: "1px solid var(--app-border)", my: "6px" }} />
      </>
    )}
    <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
      {items.map((it, i) => (
        <Box
          key={i}
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Box sx={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: it.color }} />
            <Typography sx={{ fontSize: 12.5, color: "var(--app-text-muted)" }}>{it.label}</Typography>
          </Box>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "var(--app-text)" }}>
            {it.value}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

const SeriesTooltip = ({ active, payload, label, suffix = "" }) =>
  active && payload?.length ? (
    <TooltipBox
      label={label}
      items={payload.map((p) => ({
        label: p.name,
        value: `${typeof p.value === "number" ? p.value.toLocaleString() : p.value}${suffix}`,
        color: p.color || p.stroke || p.fill,
      }))}
    />
  ) : null;

const PieTooltip = ({ active, payload, suffix = "" }) =>
  active && payload?.length ? (
    <TooltipBox
      items={[
        {
          label: payload[0].name,
          value: `${Number(payload[0].value).toLocaleString()}${suffix}`,
          color: payload[0].payload?.color,
        },
      ]}
    />
  ) : null;

const PeakTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <TooltipBox
      label={`${d.range} IST`}
      items={[
        { label: "Tests", value: d.tests.toLocaleString(), color: ACCENT },
        { label: "Critical", value: d.critical.toLocaleString(), color: "#FB8588" },
      ]}
    />
  );
};

/* ===================== interactive / static legend ===================== */
const Legend = ({ items, hidden, onToggle }) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, mt: 1 }}>
    {items.map((it) => {
      const off = hidden?.has?.(it.key);
      return (
        <Box
          key={it.label}
          onClick={onToggle && it.key ? () => onToggle(it.key) : undefined}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            cursor: onToggle && it.key ? "pointer" : "default",
            userSelect: "none",
            opacity: off ? 0.4 : 1,
            transition: "opacity .15s ease",
            "&:hover": onToggle && it.key ? { opacity: off ? 0.6 : 0.85 } : {},
          }}
        >
          <Box
            sx={{
              width: 18,
              height: 5,
              borderRadius: 8,
              backgroundColor: it.color,
              ...(off ? { filter: "grayscale(1)" } : {}),
            }}
          />
          <Typography
            sx={{
              fontSize: 12.5,
              fontWeight: 600,
              color: "var(--app-text)",
              textDecoration: off ? "line-through" : "none",
            }}
          >
            {it.label}
            {it.value != null && (
              <Box component="span" sx={{ color: "var(--app-text-muted)", fontWeight: 700, ml: 0.5 }}>
                {it.value}
              </Box>
            )}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

/* tiny dropdown styled like the rest of the analytics controls */
const MiniSelect = ({ value, onChange, options }) => (
  <FormControl size="small">
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={{
        height: 30,
        borderRadius: "8px",
        fontSize: 12.5,
        fontWeight: 700,
        color: "var(--app-text)",
        backgroundColor: "var(--app-surface-2)",
        "& .MuiOutlinedInput-notchedOutline": { border: "1px solid var(--app-border)" },
        "& .MuiSelect-select": { py: 0.4, pl: 1.25 },
      }}
    >
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value} sx={{ fontSize: 12.5 }}>
          {o.label}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

/* ===================== card frame (entrance + hover) ===================== */
/* Loading placeholders. A wave shimmer in the exact spot the value/chart will
   occupy — no spinners, no layout shift, just a hint that data is on the way. */
const ChartWave = ({ height = 240 }) => (
  <Box sx={{ height, display: "flex", flexDirection: "column", gap: 1, justifyContent: "flex-end" }}>
    <Skeleton animation="wave" variant="rectangular" height="70%" sx={{ borderRadius: 2 }} />
    <Skeleton animation="wave" variant="text" width="45%" />
  </Box>
);

const ValueWave = ({ width = 68 }) => (
  <Skeleton animation="wave" variant="text" width={width} sx={{ fontSize: 21, transform: "none", my: 0.4 }} />
);

const ChartFrame = ({ title, subtitle, icon, extra, children, delay = 0, minHeight = 300, loading = false }) => {
  const [isFull, setFull] = useFullscreen();
  return (
    <>
      <FullscreenBackdrop open={isFull} onClose={() => setFull(false)} />
      <Box
        sx={{
          height: "100%",
          minHeight,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "var(--app-surface)",
          border: "1px solid var(--app-border)",
          borderRadius: "16px",
          // boxShadow: "var(--app-shadow)",
          overflow: "hidden",
          opacity: 0,
          animation: "analyticsCardIn .5s ease forwards",
          animationDelay: `${delay}ms`,
          transition: "transform .22s ease, .22s ease, border-color .22s ease",
          "&:hover": {
            transform: "translateY(-3px)",
            // boxShadow: "0 12px 28px rgba(16,24,40,0.12)",
            borderColor: "rgba(87,81,233,0.35)",
          },
          "@keyframes analyticsCardIn": {
            from: { opacity: 0, transform: "translateY(12px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
          ...fullscreenSx(isFull),
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: 2,
            py: 1.5,
            borderBottom: "1px solid var(--app-border)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: "9px",
                display: "grid",
                placeItems: "center",
                backgroundColor: "rgba(87,81,233,0.10)",
                color: ACCENT,
                flexShrink: 0,
              }}
            >
              <Icon icon={icon} width="18" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{ fontWeight: 700, fontSize: 15, color: "var(--app-text)", fontFamily: "Figtree", lineHeight: 1.2 }}
              >
                {title}
              </Typography>
              {subtitle && (
                <Typography sx={{ fontSize: 12, color: "var(--app-text-muted)", fontFamily: "Figtree" }} noWrap>
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
            {extra}
            <FullscreenButton isFull={isFull} onToggle={() => setFull((f) => !f)} />
          </Box>
        </Box>
        <Box sx={{ p: 2, flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
          {loading ? <ChartWave /> : children}
        </Box>
      </Box>
    </>
  );
};

const EmptyChart = ({ height = 240 }) => (
  <Box
    sx={{
      height,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 1,
      color: "var(--app-text-muted)",
    }}
  >
    <Icon icon="mdi:chart-box-outline" width="34" />
    <Typography sx={{ fontSize: 13, fontFamily: "Figtree" }}>No data for the selected filters.</Typography>
  </Box>
);

/* responsive 12-col grid */
const Grid = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gap: { xs: 2, md: 2.5 },
      gridTemplateColumns: { xs: "1fr", lg: "repeat(12, 1fr)" },
    }}
  >
    {children}
  </Box>
);
const Cellz = ({ span = 12, children }) => (
  <Box sx={{ gridColumn: { lg: `span ${span}` }, minWidth: 0 }}>{children}</Box>
);

/* reusable donut with a slice that grows on hover */
const renderActiveSlice = (props) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 6}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={outerRadius + 8}
        outerRadius={outerRadius + 11}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        opacity={0.3}
      />
    </g>
  );
};

const Donut = ({ data, total, centerLabel, height = 240, suffix = "" }) => {
  const [active, setActive] = useState(-1);
  return (
    <Box sx={{ position: "relative", width: "100%", height }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            innerRadius="56%"
            outerRadius="82%"
            paddingAngle={data.length > 1 ? 2 : 0}
            stroke="var(--app-surface)"
            strokeWidth={2}
            animationDuration={900}
            activeIndex={active >= 0 ? active : undefined}
            activeShape={renderActiveSlice}
            onMouseEnter={(_, i) => setActive(i)}
            onMouseLeave={() => setActive(-1)}
          >
            {data.map((d, i) => (
              <Cell key={i} fill={d.color} style={{ outline: "none", cursor: "pointer" }} />
            ))}
          </Pie>
          <RTooltip content={<PieTooltip suffix={suffix} />} />
        </PieChart>
      </ResponsiveContainer>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        <Typography sx={{ fontSize: 24, fontWeight: 800, color: "var(--app-text)", lineHeight: 1 }}>
          {typeof total === "number" ? total.toLocaleString() : total}
        </Typography>
        <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: "var(--app-text-muted)", mt: 0.25 }}>
          {centerLabel}
        </Typography>
      </Box>
    </Box>
  );
};

const axisTick = { fontSize: 12, fill: "var(--app-text)", fontFamily: "Figtree" };
const hoverCursor = { fill: "rgba(87,81,233,0.07)" };

/* ============================== KPI band ============================== */
const KpiCard = ({ icon, label, value, sub, color, delay, loading = false }) => (
  <Box
    sx={{
      flex: "0 0 auto",
      minWidth: 150,
      backgroundColor: "var(--app-surface)",
      border: "1px solid var(--app-border)",
      borderRadius: "13px",
      // boxShadow: "var(--app-shadow)",
      pl: 1.5,
      pr: 1.5,
      py: 1.1,
      minHeight: 70,
      display: "flex",
      alignItems: "center",
      gap: 1.25,
      position: "relative",
      overflow: "hidden",
      opacity: 0,
      animation: "analyticsKpiIn .45s ease forwards",
      animationDelay: `${delay}ms`,
      transition: "transform .2s ease, .2s ease, border-color .2s ease",
      "&:hover": {
        transform: "translateY(-2px)",
        // boxShadow: "0 10px 20px rgba(16,24,40,0.1)",
        borderColor: `${color}66`,
      },
      "@keyframes analyticsKpiIn": {
        from: { opacity: 0, transform: "translateY(8px)" },
        to: { opacity: 1, transform: "translateY(0)" },
      },
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: 3,
        backgroundColor: color,
      },
    }}
  >
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: "10px",
        display: "grid",
        placeItems: "center",
        backgroundColor: `${color}1A`,
        color,
        flexShrink: 0,
      }}
    >
      <Icon icon={icon} width="19" />
    </Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          color: "var(--app-text-muted)",
          fontFamily: "Figtree",
          lineHeight: 1.25,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </Typography>
      {loading ? (
        <ValueWave />
      ) : typeof value === "object" ? (
        <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
          <Box>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 800,
                color: "#E53935",
                lineHeight: 1,
              }}
            >
              {value.stemi}
            </Typography>
            <Typography
              sx={{
                fontSize: 10,
                color: "var(--app-text-muted)",
              }}
            >
              STEMI
            </Typography>
          </Box>

          <Box
            sx={{
              width: 1,
              background: "var(--app-border)",
            }}
          />

          <Box>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 800,
                color: "#FB8C00",
                lineHeight: 1,
              }}
            >
              {value.nonStemi}
            </Typography>
            <Typography
              sx={{
                fontSize: 10,
                color: "var(--app-text-muted)",
              }}
            >
              NSTEMI
            </Typography>
          </Box>
        </Box>
      ) : typeof value === "number" ? (
        <AnimatedNumber
          value={value}
          sx={{ fontSize: 21, fontWeight: 800, color: "var(--app-text)" }}
        />
      ) : (
        <Typography
          sx={{
            fontSize: 21,
            fontWeight: 800,
            color: "var(--app-text)",
            fontFamily: "Figtree",
          }}
        >
          {value}
        </Typography>
      )}
      {sub && (
        <Typography
          sx={{
            fontSize: 10.5,
            fontWeight: 600,
            color: "var(--app-text-muted)",
            fontFamily: "Figtree",
            lineHeight: 1.25,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {sub}
        </Typography>
      )}
    </Box>
  </Box>
);

export const KpiBand = ({ filters, date }) => {
  const args = useMemo(() => buildArgs(date, filters), [date, filters]);
  const { analytics, isLoading: analyticsLoading } = useGetAnalytics(args);
  const { tables, isLoading: tablesLoading } = useGetAnalyticsTables(args);
  // The band mixes numbers from both sources, so it shimmers until both land
  // rather than briefly showing zeros that then animate to the real value.
  const loading = analyticsLoading || tablesLoading;

  const k = useMemo(() => {
    const a = analytics || {};
    const s = a.interpretationStatusCounts || {};
    const normal = num(s.normal);
    const abnormal = num(s.abnormal);
    const critical = num(s.critical);
    const retake = num(s.retake);
    const interpreted = num(a.totalInterpretedCount) || normal + abnormal + critical + retake;
    const total = num(a.totalTestsTaken);
    const pending = Math.max(0, total - interpreted);
    const newP = num(a.patientClassification?.newPatients);
    const ret = num(a.patientClassification?.returning);
    return {
      total,
      interpreted,
      pending,
      critical,
      cases: num(a.cases),
      patients: a.totalUniquePatients != null ? num(a.totalUniquePatients) : newP + ret,
      stemi: Number(tables?.summary?.stemiCount || 0),
      nonStemi: Number(tables?.summary?.nonStemiCount || 0),
      otherCritical: Math.max(0, critical - (Number(tables?.summary?.stemiCount || 0) + Number(tables?.summary?.nonStemiCount || 0))),
    };
  }, [analytics, tables?.summary]);

  const cards = [
    { icon: "mdi:heart-pulse", label: "Total Tests", value: k.total, color: "#5751E9", sub: "All ECGs recorded" },
    { icon: "mdi:check-decagram-outline", label: "Interpreted", value: k.interpreted, color: "#53B253", sub: `${pct(k.interpreted, k.total)} of total` },
    { icon: "mdi:clock-alert-outline", label: "Pending", value: k.pending, color: "#FFC46C", sub: `${pct(k.pending, k.total)} of total` },
    { icon: "mdi:alert-octagon-outline", label: "Critical", value: k.critical, color: "#FB8588", sub: `${pct(k.critical, k.interpreted)} of interpreted` },
    { icon: "mdi:folder-pulse-outline", label: "Cases Created", value: k.cases, color: "#7C6CF6", sub: "From critical / escalated" },
    // { icon: "mdi:account-group-outline", label: "Unique Patients", value: k.patients, color: "#2BB1B1", sub: "Distinct people" },
    { icon: "mdi:heart-flash", label: "STEMI", value: k.stemi, color: "#E53935", sub: "STEMI" },
    { icon: "mdi:heart-outline", label: "NSTEMI", value: k.nonStemi, color: "#E53935", sub: "NSTEMI" },
    { icon: "mdi:alert-decagram-outline", label: "Other", value: k.otherCritical, color: "#B71C1C", sub: "Other Findings" },
  ];


  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "nowrap",
        gap: { xs: 1, md: 1.5 },
        overflowX: "auto",
        pb: 0.5,
      }}
    >
      {cards.map((c, i) => (
        <KpiCard key={c.label} {...c} delay={i * 60} loading={loading} />
      ))}
    </Box>
  );
};

/* ============================ Overview charts ============================ */
export const OverviewCharts = ({ filters, date }) => {
  const granOpts = GRANULARITY_OPTIONS[date] || [{ label: "Daily", value: "day" }];
  const [gran, setGran] = useState(granOpts[0].value);
  useEffect(() => {
    setGran((GRANULARITY_OPTIONS[date] || [{ value: "day" }])[0].value);
  }, [date]);

  // hide/show individual test-type series on the volume chart
  const [hidden, setHidden] = useState(() => new Set());
  const toggle = (key) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const visibleSeries = TREND_SERIES.filter((s) => !hidden.has(s.key));

  const baseArgs = useMemo(() => buildArgs(date, filters), [date, filters]);
  const tsArgs = useMemo(
    () => buildArgs(date, filters, { granularity: gran, cases_granularity: gran }),
    [date, filters, gran],
  );

  // Hourly buckets over the range, folded into a 24h "hour of day" profile so
  // the peak staffing window stands out. Hourly over 6–12 months is thousands of
  // buckets, so cap the window to 3 months (still ample to find peak hours).
  const peakDate = date === "last_6_months" || date === "last_12_months" ? "last_3_months" : date;
  const peakCapped = peakDate !== date;
  const hourArgs = useMemo(
    () => buildArgs(peakDate, filters, { granularity: "hour", cases_granularity: "hour" }),
    [peakDate, filters],
  );

  const { analytics, isLoading: analyticsLoading } = useGetAnalytics(baseArgs);
  const { timeSeries, isLoading: tsLoading } = useGetTimeseries(tsArgs);
  const { timeSeries: hourSeries } = useGetTimeseries(hourArgs);
  // One flag for this section: its frames read from both sources.
  const loading = analyticsLoading || tsLoading;

  const trend = useMemo(() => buildTrend(timeSeries, gran), [timeSeries, gran]);
  const hasVolume = trend.some((d) => d.total > 0);
  const hasRate = trend.some((d) => d.criticalPct > 0 || d.abnormalPct > 0);
  const hasCases = trend.some((d) => d.cases > 0);

  const peak = useMemo(() => {
    const reports = hourSeries?.reports || [];
    const byHour = Array.from({ length: 24 }, (_, h) => ({
      hour: h,
      label: `${h % 12 || 12}${h < 12 ? "a" : "p"}`,
      range: `${String(h).padStart(2, "0")}:00–${String((h + 1) % 24).padStart(2, "0")}:00`,
      tests: 0,
      critical: 0,
    }));
    reports.forEach((entry) => {
      const raw = entry.day || entry.date;
      if (!raw) return;
      const h = moment(raw).tz("Asia/Kolkata").hour();
      const c = entry.counts || entry.statusCounts || {};
      byHour[h].tests += num(c.normal) + num(c.abnormal) + num(c.critical) + num(c.retake);
      byHour[h].critical += num(c.critical);
    });
    const max = byHour.reduce((m, b) => (b.tests > m.tests ? b : m), byHour[0]);
    const total = byHour.reduce((a, b) => a + b.tests, 0);
    return { byHour, max, total };
  }, [hourSeries]);

  const statusData = useMemo(() => {
    const s = analytics?.interpretationStatusCounts || {};
    return SERIES.map((m) => ({ name: m.label, value: num(s[m.key]), color: m.color })).filter((d) => d.value > 0);
  }, [analytics]);
  const statusTotal = statusData.reduce((a, b) => a + b.value, 0);

  const pipe = useMemo(() => {
    const a = analytics || {};
    const s = a.interpretationStatusCounts || {};
    const interpreted =
      num(a.totalInterpretedCount) || num(s.normal) + num(s.abnormal) + num(s.critical) + num(s.retake);
    const total = num(a.totalTestsTaken);
    const pending = Math.max(0, total - interpreted);
    return { interpreted, pending, total, avg: num(a.avgInterpretationTime) };
  }, [analytics]);
  const pipeData = [
    { name: "Interpreted", value: pipe.interpreted, color: "#53B253" },
    { name: "Pending", value: pipe.pending, color: "#FFC46C" },
  ].filter((d) => d.value > 0);

  const patientData = useMemo(() => {
    const p = analytics?.patientClassification || {};
    return [
      { name: "New", value: num(p.newPatients), color: "#5751E9" },
      { name: "Returning", value: num(p.returning), color: "#C2C9FF" },
    ].filter((d) => d.value > 0);
  }, [analytics]);
  const patientTotal = patientData.reduce((a, b) => a + b.value, 0);

  const granLabel = granOpts.find((o) => o.value === gran)?.label || "Daily";

  return (
    <Grid>
      <Cellz span={12}>
        <ChartFrame
          loading={loading}
          icon="mdi:clock-time-four-outline"
          title="Peak Test Hours"
          subtitle={`Tests by hour of day (IST)${peakCapped ? " · last 3 months" : " across the range"} — plan staffing around the busiest window.`}
          delay={0}
          extra={
            peak.total > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.25,
                  py: 0.6,
                  borderRadius: "999px",
                  backgroundColor: "rgba(87,81,233,0.10)",
                  border: "1px solid rgba(87,81,233,0.25)",
                }}
              >
                <Icon icon="mdi:trophy-outline" width="15" color={ACCENT} />
                <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: ACCENT, whiteSpace: "nowrap" }}>
                  Peak {peak.max.range} · {peak.max.tests.toLocaleString()} tests
                </Typography>
              </Box>
            )
          }
        >
          {peak.total > 0 ? (
            <>
              <Box sx={{ width: "100%", overflowX: { xs: "auto", md: "hidden" } }}>
                <Box sx={{ minWidth: { xs: 720, md: "100%" } }}>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={peak.byHour} margin={{ top: 10, right: 12, left: -18, bottom: 0 }}>
                      <CartesianGrid stroke="var(--app-border)" vertical={false} />
                      <XAxis dataKey="label" tick={{ ...axisTick, fontSize: 10.5 }} axisLine={false} tickLine={false} interval={0} />
                      <YAxis tick={axisTick} axisLine={false} tickLine={false} width={42} allowDecimals={false} />
                      <RTooltip content={<PeakTooltip />} cursor={hoverCursor} />
                      <Bar
                        dataKey="tests"
                        name="Tests"
                        barSize={20}
                        radius={[6, 6, 0, 0]}
                        activeBar={{ fillOpacity: 0.85, stroke: ACCENT, strokeWidth: 1 }}
                        animationDuration={900}
                      >
                        {peak.byHour.map((b) => (
                          <Cell
                            key={b.hour}
                            fill={b.hour === peak.max.hour ? ACCENT : "#C2C9FF"}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
              <Box
                sx={{
                  mt: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "var(--app-text-muted)",
                }}
              >
                <Icon icon="mdi:lightbulb-on-outline" width="16" color="#B26A00" />
                <Typography sx={{ fontSize: 12.5, fontFamily: "Figtree" }}>
                  Busiest window is{" "}
                  <Box component="span" sx={{ fontWeight: 800, color: "var(--app-text)" }}>
                    {peak.max.range} IST
                  </Box>
                  . Consider adding manpower around this time.
                </Typography>
              </Box>
            </>
          ) : (
            <EmptyChart height={250} />
          )}
        </ChartFrame>
      </Cellz>

      <Cellz span={8}>
        <ChartFrame
          loading={loading}
          icon="mdi:chart-areaspline"
          title="Test Volume Trend"
          subtitle={`${granLabel} interpreted reports — tap a type to filter.`}
          delay={0}
          extra={granOpts.length > 1 && <MiniSelect value={gran} onChange={setGran} options={granOpts} />}
        >
          {hasVolume ? (
            <>
              <Box sx={{ width: "100%", overflowX: { xs: "auto", md: "hidden" } }}>
                <Box sx={{ minWidth: { xs: 620, md: "100%" } }}>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={trend} margin={{ top: 10, right: 12, left: -18, bottom: 0 }}>
                      <defs>
                        {TREND_SERIES.map((s) => (
                          <linearGradient key={s.key} id={`g-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={s.color} stopOpacity={0.35} />
                            <stop offset="100%" stopColor={s.color} stopOpacity={0.02} />
                          </linearGradient>
                        ))}
                      </defs>
                      <CartesianGrid stroke="var(--app-border)" vertical={false} />
                      <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} minTickGap={18} />
                      <YAxis tick={axisTick} axisLine={false} tickLine={false} width={42} allowDecimals={false} />
                      <RTooltip content={<SeriesTooltip />} cursor={{ stroke: "var(--app-border)" }} />
                      {visibleSeries.map((s, i) => (
                        <Area
                          key={s.key}
                          type="monotone"
                          dataKey={s.key}
                          name={s.label}
                          stackId="1"
                          stroke={s.color}
                          strokeWidth={2}
                          fill={`url(#g-${s.key})`}
                          activeDot={{ r: 4, strokeWidth: 2, stroke: "var(--app-surface)" }}
                          animationDuration={900}
                          animationBegin={i * 120}
                        />
                      ))}
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
              <Legend
                items={TREND_SERIES.map((s) => ({ label: s.label, color: s.color, key: s.key }))}
                hidden={hidden}
                onToggle={toggle}
              />
            </>
          ) : (
            <EmptyChart />
          )}
        </ChartFrame>
      </Cellz>

      <Cellz span={4}>
        <ChartFrame loading={loading} icon="mdi:chart-donut" title="Report Status Mix" subtitle="Share of interpreted outcomes." delay={80}>
          {statusData.length ? (
            <>
              <Donut data={statusData} total={statusTotal} centerLabel="Interpreted" />
              <Legend
                items={statusData.map((d) => ({ label: d.name, color: d.color, value: pct(d.value, statusTotal) }))}
              />
            </>
          ) : (
            <EmptyChart />
          )}
        </ChartFrame>
      </Cellz>

      <Cellz span={8}>
        <ChartFrame
          loading={loading}
          icon="mdi:trending-up"
          title="Critical & Abnormal Rate"
          subtitle={`Share of risky reads per ${granLabel.toLowerCase()} period — watch for spikes.`}
          delay={120}
        >
          {hasRate ? (
            <>
              <Box sx={{ width: "100%", overflowX: { xs: "auto", md: "hidden" } }}>
                <Box sx={{ minWidth: { xs: 620, md: "100%" } }}>
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={trend} margin={{ top: 10, right: 12, left: -18, bottom: 0 }}>
                      <CartesianGrid stroke="var(--app-border)" vertical={false} />
                      <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} minTickGap={18} />
                      <YAxis tick={axisTick} axisLine={false} tickLine={false} width={42} unit="%" />
                      <RTooltip content={<SeriesTooltip suffix="%" />} cursor={{ stroke: "var(--app-border)" }} />
                      <Line
                        type="monotone"
                        dataKey="criticalPct"
                        name="Critical %"
                        stroke="#FB8588"
                        strokeWidth={2.5}
                        dot={false}
                        activeDot={{ r: 5, strokeWidth: 2, stroke: "var(--app-surface)" }}
                        animationDuration={900}
                      />
                      <Line
                        type="monotone"
                        dataKey="abnormalPct"
                        name="Abnormal %"
                        stroke="#FFC46C"
                        strokeWidth={2.5}
                        dot={false}
                        activeDot={{ r: 5, strokeWidth: 2, stroke: "var(--app-surface)" }}
                        animationDuration={900}
                        animationBegin={150}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
              <Legend
                items={[
                  { label: "Critical %", color: "#FB8588" },
                  { label: "Abnormal %", color: "#FFC46C" },
                ]}
              />
            </>
          ) : (
            <EmptyChart height={240} />
          )}
        </ChartFrame>
      </Cellz>

      <Cellz span={4}>
        <ChartFrame loading={loading} icon="mdi:timer-sand" title="Interpretation Pipeline" subtitle="Interpreted vs pending & turnaround." delay={160}>
          {pipe.total > 0 ? (
            <>
              <Donut data={pipeData} total={pipe.total} centerLabel="Total Tests" />
              <Legend
                items={pipeData.map((d) => ({ label: d.name, color: d.color, value: pct(d.value, pipe.total) }))}
              />
              <Box
                sx={{
                  mt: 1.25,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: "10px",
                  backgroundColor: "var(--app-surface-2)",
                  border: "1px solid var(--app-border)",
                }}
              >
                <Icon icon="mdi:timer-outline" width="16" color={ACCENT} />
                <Typography sx={{ fontSize: 12.5, color: "var(--app-text-muted)", fontWeight: 600 }}>
                  Avg turnaround
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: "var(--app-text)", ml: "auto" }}>
                  {fmtMins(pipe.avg)}
                </Typography>
              </Box>
            </>
          ) : (
            <EmptyChart />
          )}
        </ChartFrame>
      </Cellz>

      <Cellz span={8}>
        <ChartFrame
          loading={loading}
          icon="mdi:folder-pulse-outline"
          title="Cases Created"
          subtitle={`Cases opened from critical / escalated tests per ${granLabel.toLowerCase()} period.`}
          delay={200}
        >
          {hasCases ? (
            <Box sx={{ width: "100%", overflowX: { xs: "auto", md: "hidden" } }}>
              <Box sx={{ minWidth: { xs: 620, md: "100%" } }}>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={trend} margin={{ top: 10, right: 12, left: -18, bottom: 0 }}>
                    <CartesianGrid stroke="var(--app-border)" vertical={false} />
                    <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} minTickGap={18} />
                    <YAxis tick={axisTick} axisLine={false} tickLine={false} width={42} allowDecimals={false} />
                    <RTooltip content={<SeriesTooltip />} cursor={hoverCursor} />
                    <Bar
                      dataKey="cases"
                      name="Cases"
                      fill={ACCENT}
                      barSize={22}
                      radius={[6, 6, 0, 0]}
                      activeBar={{ fillOpacity: 0.85, stroke: ACCENT, strokeWidth: 1 }}
                      animationDuration={900}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          ) : (
            <EmptyChart height={240} />
          )}
        </ChartFrame>
      </Cellz>

      <Cellz span={4}>
        <ChartFrame loading={loading} icon="mdi:account-switch-outline" title="New vs Returning" subtitle="Patient mix for the range." delay={240}>
          {patientData.length ? (
            <>
              <Donut data={patientData} total={patientTotal} centerLabel="Patients" />
              <Legend
                items={patientData.map((d) => ({ label: d.name, color: d.color, value: pct(d.value, patientTotal) }))}
              />
            </>
          ) : (
            <EmptyChart />
          )}
        </ChartFrame>
      </Cellz>
    </Grid>
  );
};

/* ============================ Centre charts ============================ */
export const CentreCharts = ({ filters, date }) => {
  const args = useMemo(() => buildArgs(date, filters), [date, filters]);
  const { tables, isLoading: loading } = useGetAnalyticsTables(args);

  const centres = useMemo(() => tables?.centrePerformance || [], [tables]);
  const hubs = useMemo(() => tables?.hubPerformance || [], [tables]);

  const topCentres = useMemo(
    () =>
      [...centres]
        .map((c) => ({ name: c.centre || "—", value: num(c.totalTests) }))
        .filter((c) => c.value > 0)
        .sort((a, b) => b.value - a.value)
        .slice(0, 10)
        .reverse(),
    [centres],
  );

  const statusDist = useMemo(() => {
    const counts = {};
    centres.forEach((c) => {
      const s = c.status || "Never Used";
      counts[s] = (counts[s] || 0) + 1;
    });
    return Object.keys(STATUS_FILL)
      .map((s) => ({ name: s, value: counts[s] || 0, color: STATUS_FILL[s] }))
      .filter((d) => d.value > 0);
  }, [centres]);
  const statusTotal = statusDist.reduce((a, b) => a + b.value, 0);

  const hubBars = useMemo(
    () =>
      [...hubs]
        .map((h) => ({
          name: h.hub || "—",
          normal: num(h.normal),
          abnormal: num(h.abnormal),
          critical: num(h.critical),
          retake: num(h.retake),
        }))
        .filter((h) => h.normal + h.abnormal + h.critical + h.retake > 0)
        .slice(0, 12),
    [hubs],
  );

  const districts = useMemo(() => tables?.districtPerformance || [], [tables]);
  const districtBars = useMemo(
    () =>
      [...districts]
        .map((d) => ({ name: d.district || "—", value: num(d.totalTests) }))
        .filter((d) => d.value > 0)
        .sort((a, b) => b.value - a.value)
        .slice(0, 12)
        .reverse(),
    [districts],
  );

  return (
    <Grid>
      <Cellz span={8}>
        <ChartFrame
          loading={loading}
          icon="mdi:podium"
          title="Top Centres by Test Volume"
          subtitle="Highest-throughput spoke centres in the range."
          delay={0}
          minHeight={360}
        >
          {topCentres.length ? (
            <ResponsiveContainer width="100%" height={Math.max(260, topCentres.length * 32)}>
              <BarChart data={topCentres} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 4 }}>
                <CartesianGrid stroke="var(--app-border)" horizontal={false} />
                <XAxis type="number" tick={axisTick} axisLine={false} tickLine={false} allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ ...axisTick, fontSize: 11.5 }}
                  axisLine={false}
                  tickLine={false}
                  width={130}
                  tickFormatter={(v) => (v.length > 18 ? `${v.slice(0, 17)}…` : v)}
                />
                <RTooltip content={<SeriesTooltip />} cursor={hoverCursor} />
                <Bar
                  dataKey="value"
                  name="Total Tests"
                  fill={ACCENT}
                  barSize={16}
                  radius={[0, 6, 6, 0]}
                  activeBar={{ fillOpacity: 0.85, stroke: ACCENT, strokeWidth: 1 }}
                  animationDuration={900}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart />
          )}
        </ChartFrame>
      </Cellz>

      <Cellz span={4}>
        <ChartFrame loading={loading} icon="mdi:traffic-light-outline" title="Centre Status" subtitle="Operational state of all centres." delay={80} minHeight={360}>
          {statusDist.length ? (
            <>
              <Donut data={statusDist} total={statusTotal} centerLabel="Centres" />
              <Legend items={statusDist.map((d) => ({ label: d.name, color: d.color, value: d.value }))} />
            </>
          ) : (
            <EmptyChart />
          )}
        </ChartFrame>
      </Cellz>

      <Cellz span={12}>
        <ChartFrame
          loading={loading}
          icon="mdi:hospital-building"
          title="Hub-wise Outcome Breakdown"
          subtitle="Stacked report outcomes grouped by hub centre."
          delay={120}
        >
          {hubBars.length ? (
            <>
              <Box sx={{ width: "100%", overflowX: { xs: "auto", md: "hidden" } }}>
                <Box sx={{ minWidth: { xs: 640, md: "100%" } }}>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={hubBars} margin={{ top: 10, right: 12, left: -18, bottom: 0 }}>
                      <CartesianGrid stroke="var(--app-border)" vertical={false} />
                      <XAxis
                        dataKey="name"
                        tick={axisTick}
                        axisLine={false}
                        tickLine={false}
                        interval={0}
                        angle={hubBars.length > 6 ? -15 : 0}
                        textAnchor={hubBars.length > 6 ? "end" : "middle"}
                        height={hubBars.length > 6 ? 50 : 30}
                      />
                      <YAxis tick={axisTick} axisLine={false} tickLine={false} width={42} allowDecimals={false} />
                      <RTooltip content={<SeriesTooltip />} cursor={hoverCursor} />
                      {SERIES.map((s) => (
                        <Bar
                          key={s.key}
                          dataKey={s.key}
                          name={s.label}
                          stackId="o"
                          fill={s.color}
                          barSize={34}
                          animationDuration={900}
                          radius={s.key === "retake" ? [6, 6, 0, 0] : [0, 0, 0, 0]}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
              <Legend items={SERIES.map((s) => ({ label: s.label, color: s.color }))} />
            </>
          ) : (
            <EmptyChart />
          )}
        </ChartFrame>
      </Cellz>

      <Cellz span={12}>
        <ChartFrame
          loading={loading}
          icon="mdi:map-marker-radius-outline"
          title="Tests by District"
          subtitle="Total test volume rolled up by centre district."
          delay={160}
          minHeight={320}
        >
          {districtBars.length ? (
            <ResponsiveContainer width="100%" height={Math.max(240, districtBars.length * 30)}>
              <BarChart data={districtBars} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 4 }}>
                <CartesianGrid stroke="var(--app-border)" horizontal={false} />
                <XAxis type="number" tick={axisTick} axisLine={false} tickLine={false} allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ ...axisTick, fontSize: 11.5 }}
                  axisLine={false}
                  tickLine={false}
                  width={130}
                  tickFormatter={(v) => (v.length > 18 ? `${v.slice(0, 17)}…` : v)}
                />
                <RTooltip content={<SeriesTooltip />} cursor={hoverCursor} />
                <Bar
                  dataKey="value"
                  name="Total Tests"
                  fill="#2BB1B1"
                  barSize={16}
                  radius={[0, 6, 6, 0]}
                  activeBar={{ fillOpacity: 0.85, stroke: "#2BB1B1", strokeWidth: 1 }}
                  animationDuration={900}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart />
          )}
        </ChartFrame>
      </Cellz>
    </Grid>
  );
};

/* ============================ Clinical charts ============================ */
export const ClinicalCharts = ({ filters, date }) => {
  const args = useMemo(() => buildArgs(date, filters), [date, filters]);
  const { tables, isLoading: loading } = useGetAnalyticsTables(args);

  const retakes = useMemo(() => tables?.retakeAnalysis || [], [tables]);
  const criticals = useMemo(() => tables?.criticalCases || [], [tables]);

  const topRetake = useMemo(
    () =>
      [...retakes]
        .map((r) => ({ name: r.spokeCentre || "—", value: pctNum(r.retakePct) }))
        .filter((r) => r.value > 0)
        .sort((a, b) => b.value - a.value)
        .slice(0, 10)
        .reverse(),
    [retakes],
  );

  const reasonData = useMemo(() => {
    const tot = (key) => retakes.reduce((a, r) => a + num(r[key]), 0);
    return [
      { name: "Lead Reversal", value: tot("leadReversal"), color: REASON_COLORS[0] },
      { name: "Baseline Wander", value: tot("baselineWander"), color: REASON_COLORS[1] },
      { name: "AC Interference", value: tot("acInterference"), color: REASON_COLORS[2] },
      { name: "Lead Not Clear", value: tot("leadNotClear"), color: REASON_COLORS[3] },
    ].filter((d) => d.value > 0);
  }, [retakes]);
  const reasonTotal = reasonData.reduce((a, b) => a + b.value, 0);

  const criticalByHub = useMemo(() => {
    const counts = {};
    criticals.forEach((c) => {
      const h = c.hubCentre || c.spokeCentre || "—";
      counts[h] = (counts[h] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 12);
  }, [criticals]);

  return (
    <Grid>
      <Cellz span={7}>
        <ChartFrame
          loading={loading}
          icon="mdi:repeat-variant"
          title="Highest Retake Rate by Centre"
          subtitle="Retake % — top centres needing technique support."
          delay={0}
          minHeight={360}
        >
          {topRetake.length ? (
            <ResponsiveContainer width="100%" height={Math.max(260, topRetake.length * 32)}>
              <BarChart data={topRetake} layout="vertical" margin={{ top: 4, right: 30, left: 8, bottom: 4 }}>
                <CartesianGrid stroke="var(--app-border)" horizontal={false} />
                <XAxis type="number" tick={axisTick} axisLine={false} tickLine={false} unit="%" />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ ...axisTick, fontSize: 11.5 }}
                  axisLine={false}
                  tickLine={false}
                  width={130}
                  tickFormatter={(v) => (v.length > 18 ? `${v.slice(0, 17)}…` : v)}
                />
                <RTooltip content={<SeriesTooltip suffix="%" />} cursor={hoverCursor} />
                <Bar
                  dataKey="value"
                  name="Retake %"
                  fill="#FB8588"
                  barSize={16}
                  radius={[0, 6, 6, 0]}
                  activeBar={{ fillOpacity: 0.85, stroke: "#FB8588", strokeWidth: 1 }}
                  animationDuration={900}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart />
          )}
        </ChartFrame>
      </Cellz>

      <Cellz span={5}>
        <ChartFrame
          loading={loading}
          icon="mdi:clipboard-pulse-outline"
          title="Retake Root Causes"
          subtitle="Why retakes happen across all centres."
          delay={80}
          minHeight={360}
        >
          {reasonData.length ? (
            <>
              <Donut data={reasonData} total={reasonTotal} centerLabel="Retakes" />
              <Legend items={reasonData.map((d) => ({ label: d.name, color: d.color, value: pct(d.value, reasonTotal) }))} />
            </>
          ) : (
            <EmptyChart />
          )}
        </ChartFrame>
      </Cellz>

      <Cellz span={12}>
        <ChartFrame loading={loading} icon="mdi:alert-octagon-outline" title="Critical Cases by Hub" subtitle="Where critical ECGs are concentrating." delay={120}>
          {criticalByHub.length ? (
            <Box sx={{ width: "100%", overflowX: { xs: "auto", md: "hidden" } }}>
              <Box sx={{ minWidth: { xs: 640, md: "100%" } }}>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={criticalByHub} margin={{ top: 10, right: 12, left: -18, bottom: 0 }}>
                    <CartesianGrid stroke="var(--app-border)" vertical={false} />
                    <XAxis
                      dataKey="name"
                      tick={axisTick}
                      axisLine={false}
                      tickLine={false}
                      interval={0}
                      angle={criticalByHub.length > 6 ? -15 : 0}
                      textAnchor={criticalByHub.length > 6 ? "end" : "middle"}
                      height={criticalByHub.length > 6 ? 50 : 30}
                    />
                    <YAxis tick={axisTick} axisLine={false} tickLine={false} width={42} allowDecimals={false} />
                    <RTooltip content={<SeriesTooltip />} cursor={hoverCursor} />
                    <Bar
                      dataKey="value"
                      name="Critical Cases"
                      fill="#FB8588"
                      barSize={34}
                      radius={[6, 6, 0, 0]}
                      activeBar={{ fillOpacity: 0.85, stroke: "#FB8588", strokeWidth: 1 }}
                      animationDuration={900}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          ) : (
            <EmptyChart />
          )}
        </ChartFrame>
      </Cellz>
    </Grid>
  );
};

/* ============================ Patient charts ============================ */
export const PatientCharts = ({ filters, date }) => {
  const args = useMemo(() => buildArgs(date, filters), [date, filters]);
  const { tables, isLoading: loading } = useGetAnalyticsTables(args);
  const demo = useMemo(() => tables?.demographics || [], [tables]);

  const sortAge = (a, b) => (parseInt(a.ageGroup, 10) || 0) - (parseInt(b.ageGroup, 10) || 0);

  const byAge = useMemo(() => {
    const map = {};
    demo.forEach((d) => {
      const g = (map[d.ageGroup] ||= { ageGroup: d.ageGroup, Male: 0, Female: 0, Other: 0 });
      const gender = d.gender === "Male" || d.gender === "Female" ? d.gender : "Other";
      g[gender] += num(d.totalPatients);
    });
    return Object.values(map).sort(sortAge);
  }, [demo]);
  const hasAge = byAge.some((a) => a.Male + a.Female + a.Other > 0);

  const ageOutcome = useMemo(() => {
    const map = {};
    demo.forEach((d) => {
      const g = (map[d.ageGroup] ||= { ageGroup: d.ageGroup, normal: 0, abnormal: 0, critical: 0, retake: 0 });
      g.normal += num(d.normal);
      g.abnormal += num(d.abnormal);
      g.critical += num(d.critical);
      g.retake += num(d.retake);
    });
    return Object.values(map).sort(sortAge);
  }, [demo]);
  const hasOutcome = ageOutcome.some((a) => a.normal + a.abnormal + a.critical + a.retake > 0);

  const genderSplit = useMemo(() => {
    const counts = {};
    demo.forEach((d) => {
      const gn = d.gender === "Male" || d.gender === "Female" ? d.gender : "Other";
      counts[gn] = (counts[gn] || 0) + num(d.totalPatients);
    });
    return Object.entries(counts)
      .map(([name, value]) => ({ name, value, color: GENDER_FILL[name] || GENDER_FILL.Unknown }))
      .filter((d) => d.value > 0);
  }, [demo]);
  const genderTotal = genderSplit.reduce((a, b) => a + b.value, 0);

  return (
    <Grid>
      <Cellz span={8}>
        <ChartFrame loading={loading} icon="mdi:human-male-female" title="Patients by Age & Gender" subtitle="Unique patients across age groups." delay={0}>
          {hasAge ? (
            <>
              <Box sx={{ width: "100%", overflowX: { xs: "auto", md: "hidden" } }}>
                <Box sx={{ minWidth: { xs: 560, md: "100%" } }}>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={byAge} margin={{ top: 10, right: 12, left: -18, bottom: 0 }}>
                      <CartesianGrid stroke="var(--app-border)" vertical={false} />
                      <XAxis dataKey="ageGroup" tick={axisTick} axisLine={false} tickLine={false} />
                      <YAxis tick={axisTick} axisLine={false} tickLine={false} width={42} allowDecimals={false} />
                      <RTooltip content={<SeriesTooltip />} cursor={hoverCursor} />
                      <Bar dataKey="Male" name="Male" fill={GENDER_FILL.Male} barSize={18} radius={[5, 5, 0, 0]} activeBar={{ fillOpacity: 0.85 }} animationDuration={900} />
                      <Bar dataKey="Female" name="Female" fill={GENDER_FILL.Female} barSize={18} radius={[5, 5, 0, 0]} activeBar={{ fillOpacity: 0.85 }} animationDuration={900} animationBegin={120} />
                      <Bar dataKey="Other" name="Other" fill={GENDER_FILL.Other} barSize={18} radius={[5, 5, 0, 0]} activeBar={{ fillOpacity: 0.85 }} animationDuration={900} animationBegin={240} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
              <Legend
                items={[
                  { label: "Male", color: GENDER_FILL.Male },
                  { label: "Female", color: GENDER_FILL.Female },
                  { label: "Other", color: GENDER_FILL.Other },
                ]}
              />
            </>
          ) : (
            <EmptyChart />
          )}
        </ChartFrame>
      </Cellz>

      <Cellz span={4}>
        <ChartFrame loading={loading} icon="mdi:gender-male-female" title="Gender Split" subtitle="Unique patient distribution." delay={80}>
          {genderSplit.length ? (
            <>
              <Donut data={genderSplit} total={genderTotal} centerLabel="Patients" />
              <Legend items={genderSplit.map((d) => ({ label: d.name, color: d.color, value: pct(d.value, genderTotal) }))} />
            </>
          ) : (
            <EmptyChart />
          )}
        </ChartFrame>
      </Cellz>

      <Cellz span={12}>
        <ChartFrame loading={loading} icon="mdi:chart-bar-stacked" title="Outcomes by Age Group" subtitle="Report outcomes stacked across age bands." delay={120}>
          {hasOutcome ? (
            <>
              <Box sx={{ width: "100%", overflowX: { xs: "auto", md: "hidden" } }}>
                <Box sx={{ minWidth: { xs: 560, md: "100%" } }}>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={ageOutcome} margin={{ top: 10, right: 12, left: -18, bottom: 0 }}>
                      <CartesianGrid stroke="var(--app-border)" vertical={false} />
                      <XAxis dataKey="ageGroup" tick={axisTick} axisLine={false} tickLine={false} />
                      <YAxis tick={axisTick} axisLine={false} tickLine={false} width={42} allowDecimals={false} />
                      <RTooltip content={<SeriesTooltip />} cursor={hoverCursor} />
                      {SERIES.map((s) => (
                        <Bar
                          key={s.key}
                          dataKey={s.key}
                          name={s.label}
                          stackId="a"
                          fill={s.color}
                          barSize={40}
                          animationDuration={900}
                          radius={s.key === "retake" ? [5, 5, 0, 0] : [0, 0, 0, 0]}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
              <Legend items={SERIES.map((s) => ({ label: s.label, color: s.color }))} />
            </>
          ) : (
            <EmptyChart />
          )}
        </ChartFrame>
      </Cellz>
    </Grid>
  );
};
